import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select';
import './OverView.css';
import { FiRefreshCw } from 'react-icons/fi';

const PRIORITY = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
    CRITICAL: 'Critical',
};

const PRIORITY_SORT_WEIGHT = {
    [PRIORITY.CRITICAL]: 4,
    [PRIORITY.HIGH]: 3,
    [PRIORITY.MEDIUM]: 2,
    [PRIORITY.LOW]: 1,
};

const SORTS = {
    NEWEST: 'Newest',
    OLDEST: 'Oldest',
    PRIORITY: 'Priority',
};

const formatWhen = (d) => {
    const diffMs = Date.now() - d.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));

    if (diffMinutes < 60) {
        return `${diffMinutes} min${diffMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    } else if (diffDays < 30) {
        const hrs = diffHours % 24;
        return `${diffDays} day${diffDays !== 1 ? "s" : ""}${hrs > 0 ? ` ${hrs} hr${hrs !== 1 ? "s" : ""}` : ""} ago`;
    } else {
        const days = diffDays % 30;
        return `${diffMonths} month${diffMonths !== 1 ? "s" : ""}${days > 0 ? ` ${days} day${days !== 1 ? "s" : ""}` : ""} ago`;
    }
};


const FOCUS = {
    ALL: 'ALL',
    UNRESOLVED: 'UNRESOLVED',
    SLA: 'SLA',
    PRIORITY_LOW: 'PRIORITY_LOW',
    PRIORITY_MEDIUM: 'PRIORITY_MEDIUM',
    PRIORITY_HIGH: 'PRIORITY_HIGH',
    PRIORITY_CRITICAL: 'PRIORITY_CRITICAL',
};


const OverviewTicket = () => {
    const [search, setSearch] = useState('');
    const [categoriesSel, setCategoriesSel] = useState(null);
    const [subcatsSel, setSubcatsSel] = useState(null);
    const [centersSel, setCentersSel] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState('Newest');
    const [expandedId, setExpandedId] = useState(null);
    const [focusFilter, setFocusFilter] = useState(FOCUS.UNRESOLVED);

    const [TICKETS, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [slaIds, setSlaIds] = useState(new Set());
    const [backendSlaCount, setBackendSlaCount] = useState(0);

    const fetchTickets = async () => {
        try {
            const response = await fetch(process.env.REACT_APP_API_TICKET_OVERVIEW, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
                }
            });
            const json = await response.json();
            if (json.success) {
                const normalizePriority = (p) =>
                    p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : "Low";

                const mappedTickets = (json.data.unresolvedTickets || []).map(t => ({
                    id: `TK-${t.id}`,
                    title: t.title,
                    category: t.categoryName,
                    subcategory: t.subCategoryName,
                    status: t.status,
                    priority: normalizePriority(t.priority),
                    createdAt: new Date(t.createdAt),
                    assignee: Array.isArray(t.assignedTos) && t.assignedTos.length > 0
                        ? t.assignedTos.map(o => o.name).join(", ")
                        : (Array.isArray(t.owners) && t.owners.length > 0
                            ? t.owners.map(o => o.name).join(", ")
                            : "Unassigned"),
                    raisedBy: t.createdBy?.name || "Unknown",
                    center: t.academyName,
                    description: t.description,
                    images: t.attachments?.map(a => a.photoUrl) || [],
                    totalTimeTaken: t.totalTimeTaken, 
                    slaTime: t.slaTime,          
                    slaViolationTime: t.slaViolationTime
                }));
                setTickets(mappedTickets);
                const ids = new Set(
                    (json.data.slaViolatedTickets || []).map(x => `TK-${x.id}`)
                );
                setSlaIds(ids);
                setBackendSlaCount(json.data.slaViolatedCount || ids.size);
            }
        } catch (err) {
            console.error("Error fetching tickets:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    const CATEGORIES = [...new Set(TICKETS.map(t => t.category))];
    const SUBCATEGORIES = [...new Set(TICKETS.map(t => t.subcategory))];
    const CENTERS = [...new Set(TICKETS.map(t => t.center))];
    const catOptions = CATEGORIES.map((c) => ({ value: c, label: c }));
    const subOptions = SUBCATEGORIES.map((s) => ({ value: s, label: s }));
    const centerOptions = CENTERS.map((c) => ({ value: c, label: c }));

    const isSlaBreached = (t) => slaIds.has(t.id);

    const unresolvedCount = useMemo(
        () => TICKETS.filter((t) => t.status?.toLowerCase() !== "resolved").length,
        [TICKETS]
    );

    const slaBreaches = useMemo(
        () => TICKETS.filter(isSlaBreached).length,
        [TICKETS, slaIds]
    );

    const lowCount = useMemo(
        () => TICKETS.filter((t) => t.priority === PRIORITY.LOW).length,
        [TICKETS]
    );
    const mediumCount = useMemo(
        () => TICKETS.filter((t) => t.priority === PRIORITY.MEDIUM).length,
        [TICKETS]
    );
    const highCount = useMemo(
        () => TICKETS.filter((t) => t.priority === PRIORITY.HIGH).length,
        [TICKETS]
    );
    const criticalCount = useMemo(
        () => TICKETS.filter((t) => t.priority === PRIORITY.CRITICAL).length,
        [TICKETS]
    );


    const filteredTickets = useMemo(() => {
        let rows = [...TICKETS];

        if (focusFilter === FOCUS.UNRESOLVED) {
            rows = rows.filter((t) => t.status?.toLowerCase() !== "resolved");
        } else if (focusFilter === FOCUS.SLA) {
            rows = rows.filter(isSlaBreached);
        } else if (focusFilter === FOCUS.PRIORITY_LOW) {
            rows = rows.filter((t) => t.priority === PRIORITY.LOW);
        } else if (focusFilter === FOCUS.PRIORITY_MEDIUM) {
            rows = rows.filter((t) => t.priority === PRIORITY.MEDIUM);
        } else if (focusFilter === FOCUS.PRIORITY_HIGH) {
            rows = rows.filter((t) => t.priority === PRIORITY.HIGH);
        } else if (focusFilter === FOCUS.PRIORITY_CRITICAL) {
            rows = rows.filter((t) => t.priority === PRIORITY.CRITICAL);
        }

        const catVal = categoriesSel?.value || null;
        const subVal = subcatsSel?.value || null;
        const cenVal = centersSel?.value || null;

        if (catVal) rows = rows.filter((r) => r.category === catVal);
        if (subVal) rows = rows.filter((r) => r.subcategory === subVal);
        if (cenVal) rows = rows.filter((r) => r.center === cenVal);

        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter((r) => {
                return (
                    (r.id?.toLowerCase() || '').includes(q) ||
                    (r.title?.toLowerCase() || '').includes(q) ||
                    (r.description?.toLowerCase() || '').includes(q) ||
                    (r.raisedBy?.toLowerCase() || '').includes(q) ||
                    (r.assignee?.toLowerCase() || '').includes(q) ||
                    (r.center?.toLowerCase() || '').includes(q) ||
                    (r.category?.toLowerCase() || '').includes(q) ||
                    (r.subcategory?.toLowerCase() || '').includes(q) ||
                    (r.priority?.toLowerCase() || '').includes(q) ||
                    (r.status?.toLowerCase() || '').includes(q)
                );
            });
        }
        rows.sort((a, b) => {
            if (sortBy === SORTS.NEWEST) return b.createdAt - a.createdAt;
            if (sortBy === SORTS.OLDEST) return a.createdAt - b.createdAt;
            if (sortBy === SORTS.PRIORITY) {
                const pa = PRIORITY_SORT_WEIGHT[a.priority] || 0;
                const pb = PRIORITY_SORT_WEIGHT[b.priority] || 0;
                if (pb !== pa) return pb - pa;
                return b.createdAt - a.createdAt;
            }
            return 0;
        });

        return rows;
    }, [categoriesSel, subcatsSel, centersSel, search, sortBy, focusFilter, TICKETS, slaIds]);

    const statusBadgeClass = (s = "") => {
        const lower = s.toLowerCase();
        if (lower === "open") return "overview-status-badge overview-status-open";
        if (lower === "in progress") return "overview-status-badge overview-status-in-progress";
        if (lower === "resolved") return "overview-status-badge overview-status-resolved";
        return "overview-status-badge";
    };

    const priorityBadgeClass = (p) => {
        if (p === PRIORITY.CRITICAL) return 'overview-priority-badge overview-priority-critical';
        if (p === PRIORITY.HIGH) return 'overview-priority-badge overview-priority-high';
        if (p === PRIORITY.MEDIUM) return 'overview-priority-badge overview-priority-medium';
        return 'overview-priority-badge overview-priority-low';
    };

    const handleRowExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));
    const handleFocus = (type) => () => setFocusFilter(type);
    const cardClass = (type) => `overview-card kpi-card ${focusFilter === type ? 'is-active' : ''}`;

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const sortData = (data) => {
        const { key, direction } = sortConfig;
        if (!key) return data;

        return [...data].sort((a, b) => {
            let aValue = a[key] ?? '';
            let bValue = b[key] ?? '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            const numA = parseFloat(aValue);
            const numB = parseFloat(bValue);
            const isNumeric = !isNaN(numA) && !isNaN(numB);

            if (isNumeric) {
                aValue = numA;
                bValue = numB;
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const sortedData = sortData(filteredTickets);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);
    const totalEntries = filteredTickets.length;
    const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
    const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
    };

    const generatePageNumbers = () => {
        const pages = [];
        const totalPagesToShow = 5;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const renderPagination = () => {
        return generatePageNumbers().map((page, index) =>
            typeof page === 'number' ? (
                <button
                    key={index}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageClick(page)}
                >
                    {page}
                </button>
            ) : (
                <span key={index} className="pagination-dots">
                    {page}
                </span>
            )
        );
    };
    const priorityIcon = (p) => {
        switch (p) {
            case PRIORITY.LOW:
                return <span className="material-symbols-outlined">arrow_downward</span>;
            case PRIORITY.MEDIUM:
                return <span className="material-symbols-outlined">adjust</span>;
            case PRIORITY.HIGH:
                return <span className="material-symbols-outlined">warning</span>;
            case PRIORITY.CRITICAL:
                return <span className="material-symbols-outlined">dangerous</span>;
            default:
                return <span className="material-symbols-outlined">flag</span>;
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [categoriesSel, subcatsSel, centersSel, search, sortBy, focusFilter, rowsPerPage]);

    const formatSmartHours = (hours) => {
        if (!hours && hours !== 0) return "-";
      
        if (hours < 24) {
          return `${hours}h`;
        } else {
          const days = Math.floor(hours / 24);
          const remainingHours = hours % 24;
          return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
        }
      };
    const currentPageTickets = sortedData.slice(indexOfFirstRow, indexOfLastRow);
    const slaBreachedOnPage = currentPageTickets.filter(isSlaBreached).length;
    return (
        <div className="status-report-container">
            <div className="sales-filters-login">

                {/* Category */}
                <div className="floating-select">
                    <Select
                        name="category"
                        options={catOptions}
                        value={categoriesSel}
                        onChange={(selected) => {
                            setCategoriesSel(selected || null);
                            setSubcatsSel(null);
                        }}
                        classNamePrefix="react-select"
                        className={`sales-filter-court-booking ${categoriesSel ? "has-value" : ""}`}
                        placeholder="Choose Category"
                        isClearable
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ placeholder: (p) => ({ ...p, color: "black" }) }}
                    />
                    {categoriesSel && <label>Category</label>}
                </div>

                {/* Subcategory */}
                <div className="floating-select">
                    <Select
                        name="subcategory"
                        options={subOptions}
                        value={subcatsSel}
                        onChange={(selected) => setSubcatsSel(selected || null)}
                        isDisabled={!categoriesSel}
                        classNamePrefix="react-select"
                        className={`sales-filter-court-booking ${subcatsSel ? "has-value" : ""}`}
                        placeholder="Subcategory"
                        isClearable
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ placeholder: (p) => ({ ...p, color: "black" }) }}
                    />
                    {subcatsSel && <label>Subcategory</label>}
                </div>

                {/* Center */}
                <div className="floating-select">
                    <Select
                        name="center"
                        options={centerOptions}
                        value={centersSel}
                        onChange={(selected) => setCentersSel(selected || null)}
                        classNamePrefix="react-select"
                        className={`sales-filter-court-booking ${centersSel ? "has-value" : ""}`}
                        placeholder="Centre"
                        isClearable
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ placeholder: (p) => ({ ...p, color: "black" }) }}
                    />
                    {centersSel && <label>Centre</label>}
                </div>
                <div className="floating-select">
                    <button className="tt-action-btn" onClick={fetchTickets} style={{ padding: '11px' }}>
                        <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="overview-cards-grid">
                <div
                    className={cardClass(FOCUS.UNRESOLVED)}
                    role="button"
                    tabIndex={0}
                    onClick={handleFocus(FOCUS.UNRESOLVED)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleFocus(FOCUS.UNRESOLVED)()}
                >
                    <div className="overview-card-summary">
                        <div className="overview-card-content">
                            <div className="overview-card-icon overview-card-icon-red">
                                <span className="material-symbols-outlined">error</span>
                            </div>
                            <div>
                                <h3 className="overview-card-title">Unresolved Tickets</h3>
                                <p className="overview-card-description">{unresolvedCount} tickets pending</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={cardClass(FOCUS.SLA)}
                    role="button"
                    tabIndex={0}
                    onClick={handleFocus(FOCUS.SLA)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleFocus(FOCUS.SLA)()}
                >
                    <div className="overview-card-summary">
                        <div className="overview-card-content">
                            <div className="overview-card-icon overview-card-icon-orange">
                                <span className="material-symbols-outlined">timer</span>
                            </div>
                            <div>
                                <h3 className="overview-card-title">SLA Violations</h3>
                                {/* backend lifetime count to match API */}
                                <p className="overview-card-description">{backendSlaCount} breached SLAs</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={cardClass(FOCUS.PRIORITY_LOW)}
                    role="button"
                    tabIndex={0}
                    onClick={handleFocus(FOCUS.PRIORITY_LOW)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleFocus(FOCUS.PRIORITY_LOW)()}
                >
                    <div className="overview-card-summary">
                        <div className="overview-card-content">
                            <div className="overview-card-icon overview-card-icon-muted">
                                {priorityIcon(PRIORITY.LOW)}
                            </div>
                            <div>
                                <h3 className="overview-card-title">Low Priority</h3>
                                <p className="overview-card-description">{lowCount} low issues</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={cardClass(FOCUS.PRIORITY_MEDIUM)}
                    role="button"
                    tabIndex={0}
                    onClick={handleFocus(FOCUS.PRIORITY_MEDIUM)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleFocus(FOCUS.PRIORITY_MEDIUM)()}
                >
                    <div className="overview-card-summary">
                        <div className="overview-card-content">
                            <div className="overview-card-icon overview-card-icon-blue">
                                {priorityIcon(PRIORITY.MEDIUM)}
                            </div>
                            <div>
                                <h3 className="overview-card-title">Medium Priority</h3>
                                <p className="overview-card-description">{mediumCount} medium issues</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={cardClass(FOCUS.PRIORITY_HIGH)}
                    role="button"
                    tabIndex={0}
                    onClick={handleFocus(FOCUS.PRIORITY_HIGH)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleFocus(FOCUS.PRIORITY_HIGH)()}
                >
                    <div className="overview-card-summary">
                        <div className="overview-card-content">
                            <div className="overview-card-icon overview-card-icon-yellow">
                                {priorityIcon(PRIORITY.HIGH)}
                            </div>
                            <div>
                                <h3 className="overview-card-title">High Priority</h3>
                                <p className="overview-card-description">{highCount} high issues</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={cardClass(FOCUS.PRIORITY_CRITICAL)}
                    role="button"
                    tabIndex={0}
                    onClick={handleFocus(FOCUS.PRIORITY_CRITICAL)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleFocus(FOCUS.PRIORITY_CRITICAL)()}
                >
                    <div className="overview-card-summary">
                        <div className="overview-card-content">
                            <div className="overview-card-icon overview-card-icon-purple">
                                {priorityIcon(PRIORITY.CRITICAL)}
                            </div>
                            <div>
                                <h3 className="overview-card-title">Critical Tickets</h3>
                                <p className="overview-card-description">{criticalCount} critical issues</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="tickets-feed">
                {loading ? (
                    <div className="premium-loader">
                        <div className="progress-track">
                            <div className="progress-bar"></div>
                        </div>
                        <div className="status-text">
                            <span>L</span>
                            <span>O</span>
                            <span>A</span>
                            <span>D</span>
                            <span>I</span>
                            <span>N</span>
                            <span>G</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="tickets-feed-header">
                            <div className="search-create-container">
                                <div className="input-container">
                                    <input
                                        type="text"
                                        id="searchInput"
                                        placeholder=" "
                                        className={`search-input ${search ? "has-value" : ""}`}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <label htmlFor="searchInput" className="floating-label">
                                        🔍 Search
                                    </label>
                                </div>
                            </div>

                            <div className="overview-tickets-controls">
                                <select
                                    className="overview-sort-select"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    aria-label="Sort tickets"
                                >
                                    <option>{SORTS.NEWEST}</option>
                                    <option>{SORTS.OLDEST}</option>
                                    <option>{SORTS.PRIORITY}</option>
                                </select>
                            </div>
                        </div>

                        {filteredTickets.length === 0 && (
                            <div className="empty-state">
                                <div className="empty-state-icon material-symbols-outlined">inbox</div>
                                <p>No tickets found.</p>
                            </div>
                        )}

                        {sortedData.slice(indexOfFirstRow, indexOfLastRow).map((t) => {
                            const expanded = expandedId === t.id;

                            return (
                                <article
                                    key={t.id}
                                    className={`ticket-card ${expanded ? 'is-expanded' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleRowExpand(t.id)}
                                    onKeyDown={(e) =>
                                        (e.key === 'Enter' || e.key === ' ') && handleRowExpand(t.id)
                                    }
                                    aria-expanded={expanded}
                                >
                                    <button
                                        className="ticket-expand-handle"
                                        aria-label={expanded ? 'Collapse ticket' : 'Expand ticket'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowExpand(t.id);
                                        }}
                                    >
                                        <span className="material-symbols-outlined chevron" aria-hidden="true">
                                            chevron_right
                                        </span>
                                    </button>
                                    <div className="ticket-card-head">
                                        <div className="ticket-card-title">
                                            <span className="ticket-id">#{t.id}</span>
                                            <span className="ticket-title-text">{t.title}</span>
                                        </div>

                                        <div className="ticket-chips">
                                            <span className={priorityBadgeClass(t.priority)}>{t.priority}</span>
                                            {isSlaBreached(t) && (
                                                <span className="chip chip-warning">SLA Overdue</span>
                                            )}
                                            <span className={statusBadgeClass(t.status)}>{t.status}</span>
                                        </div>
                                    </div>

                                    <div className="ticket-meta-strip">
                                        <span className="meta">
                                            <span className="material-symbols-outlined">category</span>
                                            {t.category} → {t.subcategory}
                                        </span>
                                        <span className="meta">
                                            <span className="material-symbols-outlined">location_on</span>
                                            {t.center}
                                        </span>
                                        <span className="meta" title={t.createdAt.toLocaleString()}>
                                            <span className="material-symbols-outlined">schedule</span>
                                            {formatWhen(t.createdAt)}
                                        </span>
                                        <span className="meta">
                                            <span className="material-symbols-outlined">person</span>
                                            {t.assignee}
                                        </span>
                                    </div>

                                    {expanded && (
                                        <div className="ticket-details">
                                            <div className="ticket-details-grid">
                                                <div className="ticket-details-block">
                                                    <h4 className="ticket-details-title">Description</h4>
                                                    <p className="ticket-details-text">{t.description}</p>
                                                </div>

                                                <div className="ticket-details-block">
                                                    <h4 className="ticket-details-title">People</h4>
                                                    <ul className="ticket-meta-list">
                                                        <li><strong>Reported by:</strong> {t.raisedBy}</li>
                                                        <li><strong>Assignee:</strong> {t.assignee}</li>
                                                    </ul>
                                                </div>

                                                <div className="ticket-details-block">
                                                    <h4 className="ticket-details-title">Meta</h4>
                                                    <ul className="ticket-meta-list">
                                                        <li><strong>Category:</strong> {t.category}</li>
                                                        <li><strong>Subcategory:</strong> {t.subcategory}</li>
                                                        <li><strong>Centre:</strong> {t.center}</li>
                                                        <li><strong>Status:</strong> {t.status}</li>
                                                        <li><strong>Priority:</strong> {t.priority}</li>
                                                        <li><strong>Created:</strong> {t.createdAt.toLocaleString()}</li>
                                                        <li><strong>SLA Breached:</strong> {isSlaBreached(t) ? 'Yes' : 'No'}</li>
                                                        <li><strong>Total Time:</strong> {formatSmartHours(t.totalTimeTaken)}</li>
                                                        <li><strong>SLA Time:</strong> {formatSmartHours(t.slaTime)}</li>
                                                        {isSlaBreached(t) && (
                                                            <li><strong>SLA Violation Time:</strong> {formatSmartHours(t.slaViolationTime)}</li>
                                                        )}
                                                    </ul>
                                                </div>

                                                <div className="ticket-details-block">
                                                    <h4 className="ticket-details-title">Attachments</h4>
                                                    {t.images?.length ? (
                                                        <div className="ticket-images">
                                                            {t.images.map((src, i) => (
                                                                <a
                                                                    key={i}
                                                                    href={src}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="ticket-image-link"
                                                                >
                                                                    <img
                                                                        src={src}
                                                                        alt={`attachment-${i + 1}`}
                                                                        className="ticket-image"
                                                                    />
                                                                </a>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="ticket-details-text ticket-details-muted">
                                                            No images or videos uploaded
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </article>
                            );
                        })}

                        <div className="pagination">
                            <div className="showing-entries">
                                Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
                            </div>
                            <button
                                className="prev-button"
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            {renderPagination()}
                            <button
                                className="next-button"
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}
            </div>

        </div>
    );
};

export default OverviewTicket;
